import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link } from 'react-router-dom';

export default function ExpenseTrackerPromo() {
  return (
    <>
      <Helmet>
        <title>1099 Expense Tracker - Audit-Proof Your Deductions | Free1099Calc</title>
        <meta name="description" content="Track your 1099 business expenses with real-time audit risk scoring. Know exactly which deductions are safe and what documentation you need. $5.99/month subscription." />
        <meta name="keywords" content="1099 expense tracker, tax deduction tracker, audit risk score, self-employed expenses, freelance expense tracking" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=2070"
            alt="Expense tracking"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 via-primary-800/90 to-slate-900/95"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-2xl mr-2">💰</span>
              <span className="text-sm font-semibold">NEW: Audit-Proof Expense Tracking</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Stop Guessing.<br />
              <span className="text-blue-300">Know Your Risk.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              The only expense tracker built specifically for 1099 contractors that gives you a real-time audit risk score, AI-powered tax guidance, and tells you exactly what documentation you need to defend every deduction.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Link
                to="/tools/expense-tracker/signup"
                className="inline-flex items-center px-10 py-5 bg-white text-primary-900 rounded-lg font-bold hover:bg-blue-50 transition-colors text-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
              >
                Try Free - See Your Risk Score
                <svg className="ml-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a
                href="#pricing"
                className="inline-flex items-center px-10 py-5 bg-white/10 backdrop-blur-sm text-white rounded-lg font-bold hover:bg-white/20 transition-colors text-xl border-2 border-white/30"
              >
                See Pricing
              </a>
            </div>

            <div className="text-blue-200 text-lg">
              <p>
                ✓ Free for 5 expenses (no credit card) &nbsp;|&nbsp; ✓ $5.99/month for unlimited &nbsp;|&nbsp; ✓ Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Problem with Current Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Generic expense trackers weren't built for the unique challenges of 1099 contractors
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-md border-2 border-red-100">
              <div className="text-4xl mb-4">😰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Audit Anxiety</h3>
              <p className="text-gray-600">
                You track expenses but have no idea if they'll survive an IRS audit. Are you being too aggressive? Too conservative?
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md border-2 border-red-100">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Missing Documentation</h3>
              <p className="text-gray-600">
                You write off meals and travel but don't know what business purpose notes the IRS expects. Find out during an audit? Too late.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md border-2 border-red-100">
              <div className="text-4xl mb-4">💸</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expensive Subscriptions</h3>
              <p className="text-gray-600">
                QuickBooks ($180/year), FreshBooks ($228/year), Keeper ($192/year). Why pay forever for something you should own?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Introducing Audit Risk Scoring
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Track expenses like normal, but get instant feedback on what's safe and what needs better documentation
            </p>
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">See Your Risk Score in Real-Time</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <strong className="text-gray-900">0-100 Risk Score</strong>
                      <p className="text-gray-600">Instantly see if you're in the green zone (safe) or red zone (audit bait)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <strong className="text-gray-900">Specific Red Flags</strong>
                      <p className="text-gray-600">Know exactly which expenses raise concerns (home office too high, meals undocumented, etc.)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <strong className="text-gray-900">Action Steps</strong>
                      <p className="text-gray-600">Get specific recommendations: "Add mileage log for this trip" or "Document attendees for this meal"</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-xl">
                <div className="text-center mb-4">
                  <div className="inline-block relative">
                    <svg className="w-40 h-40" viewBox="0 0 200 200">
                      <circle cx="100" cy="100" r="80" fill="none" stroke="#e5e7eb" strokeWidth="20" />
                      <circle
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="20"
                        strokeDasharray="150.72 502.4"
                        strokeLinecap="round"
                        transform="rotate(-90 100 100)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-4xl font-bold text-gray-900">28</span>
                      <span className="text-sm text-gray-500">/ 100</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                      Low Risk
                    </span>
                    <p className="text-gray-600 mt-2 text-sm">Conservative approach. Well-positioned for an audit.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need to Stay Audit-Ready
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "🤖",
                title: "AI Tax Assistant",
                description: "Get instant answers about deductions, documentation, and audit risk from our AI chatbot trained on IRS guidelines"
              },
              {
                icon: "📊",
                title: "Smart Categorization",
                description: "18 IRS-compliant expense categories with descriptions and guidance"
              },
              {
                icon: "🎯",
                title: "Risk Detection",
                description: "8 different audit risk factors analyzed in real-time"
              },
              {
                icon: "📝",
                title: "Documentation Prompts",
                description: "Know exactly what business purpose notes you need for each expense"
              },
              {
                icon: "💡",
                title: "Specific Recommendations",
                description: "Get actionable tips: 'Keep detailed mileage logs' or 'Use credit cards for better records'"
              },
              {
                icon: "📈",
                title: "Expense Ratios",
                description: "See your spending as % of income - catch red flags before the IRS does"
              },
              {
                icon: "🔒",
                title: "Local Storage",
                description: "Your data stays on your device - no cloud, no security concerns"
              },
              {
                icon: "📄",
                title: "PDF Export",
                description: "Download IRS-ready expense reports organized by category"
              },
              {
                icon: "💳",
                title: "Flexible Billing",
                description: "Just $5.99/month - cancel anytime, no long-term commitment"
              },
              {
                icon: "⚡",
                title: "Instant Updates",
                description: "See your risk score update as you add expenses - no waiting"
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-3">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Start free. Upgrade for $5.99/month. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Tier */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free Trial</h3>
                <div className="text-5xl font-bold text-gray-900 mb-2">$0</div>
                <p className="text-gray-600">Try before you buy</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700"><strong>5 expenses</strong> to test it out</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Real-time audit risk score</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">2 risk factors shown</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-gray-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-400">No PDF export</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-gray-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-400">Limited to 5 expenses & 5 AI questions</span>
                </li>
              </ul>

              <Link
                to="/tools/expense-tracker/signup"
                className="block w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-center"
              >
                Try Free - No Card Required
              </Link>
            </div>

            {/* Premium */}
            <div className="bg-gradient-to-br from-primary-600 to-blue-600 rounded-2xl p-8 shadow-2xl border-2 border-primary-400 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="inline-block px-4 py-1 bg-yellow-400 text-gray-900 rounded-full text-sm font-bold shadow-lg">
                  ⭐ BEST VALUE
                </span>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Premium</h3>
                <div className="text-5xl font-bold text-white mb-2">$5.99<span className="text-2xl">/mo</span></div>
                <p className="text-blue-100">Cancel anytime • No commitment</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white"><strong>Unlimited AI questions</strong> - ask anything about your taxes</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white"><strong>Unlimited expenses</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white">Full audit risk report</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white">All risk factors & recommendations</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white">PDF export for tax time</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white"><strong>Cancel anytime</strong> - no long-term commitment</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-white">Future updates included</span>
                </li>
              </ul>

              <Link
                to="/tools/expense-tracker/login"
                className="block w-full px-6 py-3 bg-white text-primary-900 rounded-lg font-bold hover:bg-blue-50 transition-colors text-center shadow-xl"
              >
                Get Premium - $5.99
              </Link>

              <p className="text-center text-blue-100 text-sm mt-4">
                💳 Secure payment via Stripe
              </p>
            </div>
          </div>

          {/* Comparison Note */}
          <div className="mt-12 text-center bg-slate-50 rounded-xl p-8">
            <h4 className="text-xl font-bold text-gray-900 mb-4">💰 Save $108-270/Year vs. Competitors</h4>
            <div className="grid md:grid-cols-4 gap-6 text-sm">
              <div>
                <div className="font-semibold text-gray-900 mb-2">Our Tool</div>
                <div className="text-green-600 font-bold">$5.99/month</div>
                <div className="text-gray-600">Cancel anytime</div>
              </div>
              <div>
                <div className="font-semibold text-gray-900 mb-2">QuickBooks</div>
                <div className="text-gray-900">$15/month</div>
                <div className="text-gray-600">$180/year</div>
              </div>
              <div>
                <div className="font-semibold text-gray-900 mb-2">FreshBooks</div>
                <div className="text-gray-900">$19/month</div>
                <div className="text-gray-600">$228/year</div>
              </div>
              <div>
                <div className="font-semibold text-gray-900 mb-2">Keeper Tax</div>
                <div className="text-gray-900">$16/month</div>
                <div className="text-gray-600">$192/year</div>
              </div>
            </div>
            <p className="mt-6 text-gray-600">
              <strong>Other tools charge $15-30/month.</strong> We're just $5.99/month with no contracts.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "How does the audit risk score work?",
                a: "We analyze 8 different factors that the IRS looks at during audits: expense ratios, home office deductions, vehicle expenses, meals & entertainment, travel, documentation quality, and more. Each factor is weighted based on IRS audit triggers."
              },
              {
                q: "Is my data secure?",
                a: "Yes! All your expense data is stored locally on your device using browser localStorage. Nothing is sent to any server. Your financial information never leaves your computer."
              },
              {
                q: "Can I export my expenses?",
                a: "Premium users can export expenses as a PDF report organized by IRS Schedule C categories, ready to share with your accountant or use for filing."
              },
              {
                q: "Do I need accounting knowledge?",
                a: "No! We provide descriptions for all 18 expense categories and prompt you for the documentation you need. It's designed for freelancers, not accountants."
              },
              {
                q: "What if I don't like it?",
                a: "Try it free first! Add 5 expenses and see your audit risk score before paying anything. No credit card required for the trial."
              },
              {
                q: "Is this tax advice?",
                a: "No, this is an educational tool to help you understand risk factors. Always consult with a licensed CPA or tax professional for specific tax advice."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-900 via-primary-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to File With Confidence?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join <strong>1,000+ freelancers and contractors</strong> who track expenses confidently and reduce audit anxiety.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/tools/expense-tracker/signup"
              className="inline-flex items-center px-10 py-5 bg-white text-primary-900 rounded-lg font-bold hover:bg-blue-50 transition-colors text-xl shadow-xl"
            >
              Get Your Risk Score Free
              <svg className="ml-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>

          <p className="text-blue-200">
            No credit card required for free trial • Then $5.99/month, cancel anytime
          </p>
        </div>
      </section>
    </>
  );
}
